import React from 'react';
import {
    TableCell,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Box,
    Stack
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import i18n from '../../i18n';
import { GRID_TEXT_CLAMP_CLASS } from '../../utils/gridColumns';

const styles = {
    controllerCell: {
        backgroundColor: 'background.neutral',
        fontWeight: 500
    },
    preview: {
        // Opens the full payload: underlined like a link so it reads as one.
        cursor: 'pointer',
        textDecoration: 'underline dotted',
        textUnderlineOffset: 3,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: 260,
        display: 'inline-block',
        fontFamily: 'monospace',
        fontSize: 12,
        userSelect: 'text'
    },
    jsonBox: {
        fontFamily: 'monospace',
        fontSize: 12,
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        userSelect: 'text',
        borderRadius: 2,
        padding: 2,
        backgroundColor: 'grey.900',
        color: 'grey.300',
        minHeight: 240,
        maxHeight: '60vh',
        overflow: 'auto'
    },
    hint: {
        opacity: 0.75,
        fontSize: 12,
        marginTop: 8
    }
};

const MAX_PREVIEW_LENGTH = 80;

function safeParseJson(value) {
    if (value && typeof value === 'object') return value;

    if (typeof value === 'string') {
        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    }
    return null;
}

function stringifyCompact(obj) {
    try {
        return JSON.stringify(obj);
    } catch {
        return String(obj);
    }
}

function stringifyPretty(obj) {
    try {
        return JSON.stringify(obj, null, 2);
    } catch {
        return String(obj);
    }
}

async function copyToClipboard(text) {
    if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
    }

    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'absolute';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
}

function downloadText(filename, text) {
    const safeName = String(filename || 'data.json').trim() || 'data.json';
    const finalName = safeName.endsWith('.json')
        ? safeName
        : `${safeName}.json`;

    const blob = new Blob([text], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = finalName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

function resolveFileName(field, parsedValue) {
    const raw = field?.fileName;

    // 1) function: (ctx) => string
    if (typeof raw === 'function') {
        try {
            const name = raw({ field, value: parsedValue });
            if (name) return String(name);
        } catch {
            // ignore
        }
    }

    // 2) string
    if (typeof raw === 'string' && raw.trim()) return raw.trim();

    // 3) default: use column id
    const id = field?.id || 'json';
    return `${id}.json`;
}

export default class GridJsonCell extends React.Component {
    state = {
        open: false,
        copied: false,
        copyError: false
    };

    openDialog = () => {
        this.setState({ open: true, copied: false, copyError: false });
    };

    closeDialog = () => {
        this.setState({ open: false, copied: false, copyError: false });
    };

    handleCopy = async (text) => {
        try {
            const ok = await copyToClipboard(text);
            this.setState({ copied: Boolean(ok), copyError: !ok });
            if (ok) setTimeout(() => this.setState({ copied: false }), 1200);
        } catch {
            this.setState({ copied: false, copyError: true });
        }
    };

    render() {
        const field = this.props.field || {};
        const { id, needStyle } = field;
        const baseStyle = this.props.styles;
        const value = this.props.value;
        // const value = {
        //     requestId: 'aml-9f3a7c12',
        //     checkedAt: '2025-12-29T18:42:11Z',
        //     provider: 'Chainalysis',
        //     result: 'REVIEW',
        //     riskScore: 72,
        //     riskLevel: 'HIGH',
        //     subject: {
        //         type: 'individual',
        //         firstName: 'Ivan',
        //         lastName: 'Petrov',
        //         dateOfBirth: '1987-04-19',
        //         nationality: 'RU',
        //         residencyCountry: 'MD',
        //         identifiers: {
        //             passport: {
        //                 number: '123456789',
        //                 country: 'RU'
        //             },
        //             taxId: 'RU1234567890'
        //         }
        //     },
        //     transaction: {
        //         orderUuid: '3b6b7d2e-7a9c-4c42-8d15-3b9f1e91c012',
        //         amount: 12450.75,
        //         currency: 'EUR',
        //         direction: 'IN',
        //         method: 'CARD',
        //         createdAt: '2025-12-29T18:41:55Z',
        //         counterparty: {
        //             type: 'merchant',
        //             name: 'Example Trading LTD',
        //             country: 'CY'
        //         }
        //     },
        //     screening: {
        //         sanctions: {
        //             matched: true,
        //             lists: [
        //                 {
        //                     list: 'EU Consolidated',
        //                     matchScore: 0.91,
        //                     matchedName: 'Ivan Petrov'
        //                 }
        //             ]
        //         },
        //         pep: {
        //             matched: false
        //         },
        //         adverseMedia: {
        //             matched: true,
        //             articles: [
        //                 {
        //                     title: 'Investigation into offshore accounts',
        //                     source: 'Example News',
        //                     publishedAt: '2021-06-14',
        //                     risk: 'MEDIUM'
        //                 }
        //             ]
        //         }
        //     },
        //     decision: {
        //         status: 'MANUAL_REVIEW',
        //         reasonCodes: ['SANCTIONS_MATCH', 'HIGH_RISK_COUNTRY'],
        //         recommendedActions: [
        //             'REQUEST_ADDITIONAL_DOCUMENTS',
        //             'ENHANCED_DUE_DILIGENCE'
        //         ]
        //     },
        //     rawProviderResponse: {
        //         confidence: 0.87,
        //         engineVersion: 'v4.12.3',
        //         processingTimeMs: 842
        //     }
        // };

        const enableCopy =
            typeof field.enableCopy === 'boolean' ? field.enableCopy : true;
        const enableDownload =
            typeof field.enableDownload === 'boolean'
                ? field.enableDownload
                : false;

        // No payload is not a broken one: an event without a body showed the
        // red "Invalid JSON" a malformed value gets.
        if (value === null || value === undefined || value === '') {
            return (
                <TableCell key={id} align="left" sx={baseStyle}>
                    <Typography variant="body2" color="text.secondary">
                        —
                    </Typography>
                </TableCell>
            );
        }

        const parsed = safeParseJson(value);

        if (!parsed) {
            return (
                <TableCell key={id} align="left" sx={baseStyle}>
                    <Typography variant="body2" color="error" noWrap>
                        Invalid JSON
                    </Typography>
                </TableCell>
            );
        }

        const compact = stringifyCompact(parsed);
        const pretty = stringifyPretty(parsed);

        const isLong = compact.length > MAX_PREVIEW_LENGTH;
        const previewText = isLong
            ? compact.slice(0, MAX_PREVIEW_LENGTH) + '…'
            : compact;

        const dialogTitle = i18n.t(`columns.${field.label}`);
        const fileName = resolveFileName(field, parsed);
        // Only the outcome of Copy: a standing "you can select text and copy
        // manually" restated the button next to it.
        let hintText = null;
        if (this.state.copyError) hintText = i18n.t('text.copyFailed');
        else if (this.state.copied) hintText = i18n.t('text.copied');

        return (
            <>
                <TableCell
                    key={id}
                    align="left"
                    sx={{
                        ...(needStyle ? styles.controllerCell : {}),
                        ...baseStyle
                    }}
                >
                    <Typography
                        sx={styles.preview}
                        className={GRID_TEXT_CLAMP_CLASS}
                        onClick={this.openDialog}
                    >
                        {previewText}
                    </Typography>
                </TableCell>

                <Dialog
                    open={this.state.open}
                    onClose={this.closeDialog}
                    // Sized to the payload, up to md: a three-key object sat
                    // in a 900px box of white.
                    maxWidth="md"
                    PaperProps={{ sx: { minWidth: { sm: 400 } } }}
                >
                    <DialogTitle
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                        <Box sx={{ flexGrow: 1 }}>{dialogTitle}</Box>

                        <Stack direction="row" spacing={0.5}>
                            <IconButton
                                aria-label="close"
                                onClick={this.closeDialog}
                                size="small"
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </DialogTitle>

                    <DialogContent dividers>
                        <Box sx={styles.jsonBox}>{pretty}</Box>

                        {hintText && (
                            <Typography
                                sx={styles.hint}
                                color={
                                    this.state.copyError
                                        ? 'error'
                                        : 'success.main'
                                }
                            >
                                {hintText}
                            </Typography>
                        )}
                    </DialogContent>

                    <DialogActions>
                        {enableCopy && (
                            <Button onClick={() => this.handleCopy(pretty)}>
                                {i18n.t('text.copy')}
                            </Button>
                        )}

                        {enableDownload && (
                            <Button
                                onClick={() => downloadText(fileName, pretty)}
                                startIcon={<DownloadIcon />}
                            >
                                {i18n.t('text.downloadJson')}
                            </Button>
                        )}

                        <Button onClick={this.closeDialog} variant="contained">
                            {i18n.t('buttons.close')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    }
}
